/** @jsx jsx */
import { Button } from '@datacamp/waffles/button';
import { Heading } from '@datacamp/waffles/heading';
import { mediaQuery } from '@datacamp/waffles/helpers';
import { useMediaQuery } from '@datacamp/waffles/hooks';
import { ArrowRight } from '@datacamp/waffles/icon';
import { Paragraph } from '@datacamp/waffles/paragraph';
import { tokens } from '@datacamp/waffles/tokens';
import { jsx } from '@emotion/react';
import raven from 'raven-js';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import BrowserNotificationImage from '../../../assets/images/browser_notification.svg';
import config from '../../../config';
import ApiClient from '../../../helpers/ApiClient';
import { getCampusAppClient } from '../../../helpers/getClients';
import { STORAGE_KEYS, useStorage } from '../../../helpers/useStorage';
import { useSelector } from '../../../interfaces/State';
import { selectUserSettings } from '../../../redux/selectors';
import type { OneSignal } from '../../../window';
import DailyStreakScreenWrapper from '../DailyStreakScreenWrapper';

const DAYS_IN_MS = 86_400_000;
const BROWSER_NOTIFICATION_PERMISSION_INTERVAL = 30 * DAYS_IN_MS;

const BrowserNotificationScreenInner: React.FC<{
  oneSignal: OneSignal;
  onExit: () => void;
  userId: number;
}> = ({ onExit, oneSignal, userId }) => {
  const { t } = useTranslation();
  const autoFocusRef = useRef(null);
  const { isAboveSmall } = useMediaQuery();
  const [isRequestingPermissions, setIsRequestingPermissions] = useState(false);

  const { setValue: setNotificationPermissionDenied } = useStorage(
    STORAGE_KEYS.BROWSER_NOTIFICATIONS_DENIED,
    0,
  );

  useEffect(() => {
    window.dataLayer.push({
      event: 'heap_event',
      heap_event_name: 'Learn - Campus - Browser Notification Screen Viewed',
      heap_event_properties: {
        app_id: config.appName,
        event_category: 'learning',
        identity: userId,
      },
    });
  }, [userId]);

  const handleAllowReminder = async () => {
    try {
      setIsRequestingPermissions(true);
      await oneSignal.Notifications.requestPermission();
      if (oneSignal.Notifications.permission) {
        await getCampusAppClient()
          .execute(ApiClient.endpoint.setNotificationPermission())
          .toPromise();
      }
    } catch (error) {
      raven.captureException(error, { extra: { userId } });
    } finally {
      setIsRequestingPermissions(false);
      onExit();
    }
  };

  const handleNotForNow = () => {
    setNotificationPermissionDenied(Date.now());
    onExit();
  };

  return (
    <DailyStreakScreenWrapper initialFocus={autoFocusRef}>
      <div
        css={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexGrow: 1,
          flexDirection: 'column',
          [mediaQuery.aboveSmall]: { flexGrow: 0 },
        }}
      >
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
          }}
        >
          <div
            css={{
              position: 'relative',
              width: '100%',
              maxWidth: 406,
              aspectRatio: '12 / 7',
              marginBottom: tokens.spacingNew.large,
            }}
          >
            <img
              src={BrowserNotificationImage}
              css={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
              alt={t('DailyStreakModal.browserNotification.imageAlt')}
            />
          </div>

          <Heading
            data-testid="learn-reminder-header"
            css={{
              fontSize: '32px',
              textAlign: 'center',
            }}
          >
            {t('DailyStreakModal.browserNotification.title')}
          </Heading>

          <Paragraph css={{ textAlign: 'center' }}>
            {t('DailyStreakModal.browserNotification.text')}
          </Paragraph>
        </div>
      </div>

      <div
        css={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          flexWrap: 'wrap',
          gap: tokens.spacing.medium,
          position: 'relative',
          marginTop: tokens.spacing.xlarge,
        }}
      >
        <Button
          css={{
            '&:focus': {
              outline: `${tokens.borderWidth.thin} solid ${tokens.colors.blue}`,
            },
          }}
          variant="regularOutline"
          fullWidth={!isAboveSmall}
          onClick={handleNotForNow}
          data-trackid="campus-browser-notification-skip"
          tabIndex={0}
          disabled={isRequestingPermissions}
        >
          {t('DailyStreakModal.browserNotification.skipReminder')}
        </Button>

        <Button
          css={{
            '&:focus': {
              outline: `${tokens.borderWidth.thin} solid ${tokens.colors.blue}`,
            },
          }}
          variant="cta"
          iconRight={isAboveSmall ? <ArrowRight /> : undefined}
          fullWidth={!isAboveSmall}
          onClick={handleAllowReminder}
          data-trackid="campus-browser-notification-allow-reminder"
          tabIndex={0}
          disabled={isRequestingPermissions}
        >
          {t('DailyStreakModal.browserNotification.allowReminder')}
        </Button>
      </div>
    </DailyStreakScreenWrapper>
  );
};

const BrowserNotificationScreen: React.FC<{ onExit: () => void }> = ({
  onExit,
}) => {
  const userId: number | undefined = useSelector(selectUserSettings).get('id');
  const { value: lastTimeBrowserNotificationWasDenied } = useStorage(
    STORAGE_KEYS.BROWSER_NOTIFICATIONS_DENIED,
    0,
  );
  const wasAlreadyDeniedInInterval =
    Date.now() - lastTimeBrowserNotificationWasDenied <
    BROWSER_NOTIFICATION_PERMISSION_INTERVAL;

  const oneSignal = window?.OneSignal;

  const userIsAlreadyLoggedInOneSignal = !!oneSignal?.User?.externalId;
  const [isLoggingInOneSignal, setIsLoggingInOneSignal] = useState(false);
  const [oneSignalLoginHasError, setOneSignalLoginHasError] = useState(false);

  useEffect(() => {
    if (!oneSignal || !userId || userIsAlreadyLoggedInOneSignal) {
      return;
    }

    setIsLoggingInOneSignal(true);
    oneSignal
      .login(String(userId))
      .catch((error) => {
        setOneSignalLoginHasError(true);
        raven.captureException(error, { extra: { userId } });
      })
      .finally(() => {
        setIsLoggingInOneSignal(false);
      });
  }, [oneSignal, userId, userIsAlreadyLoggedInOneSignal]);

  if (
    !userId ||
    oneSignalLoginHasError ||
    wasAlreadyDeniedInInterval ||
    !oneSignal ||
    !oneSignal.Notifications.isPushSupported() ||
    oneSignal.Notifications.permissionNative !== 'default'
  ) {
    // after onExit the component will not be updated anymore
    onExit();
    return null;
  }

  if (isLoggingInOneSignal || !userIsAlreadyLoggedInOneSignal) {
    return (
      <div
        css={{
          width: '100%',
          height: '100%',
          backgroundColor: tokens.colors.navy,
          position: 'absolute',
          overflow: 'hidden',
          top: 0,
          left: 0,
          zIndex: 999,
        }}
        data-testid="browser-notification-screen-loading"
      />
    );
  }

  return (
    <BrowserNotificationScreenInner
      oneSignal={oneSignal as OneSignal}
      onExit={onExit}
      userId={userId as number}
    />
  );
};

export default BrowserNotificationScreen;
